import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Link, useOutletContext } from 'react-router-dom';
import {
  validateCourseTitle,
  validateTopicPrompt,
  COURSE_TITLE_MIN,
  COURSE_TITLE_MAX,
  TOPIC_PROMPT_MIN,
  TOPIC_PROMPT_MAX,
} from '@courseai/domain';
import type { CourseStatus } from '@courseai/domain';
import type { ProjectTabContext } from '../ProjectDetail';
import { AgentGenerateSection } from '../AgentGenerateSection';
import { useT, type I18n, type TranslationKey } from '../../i18n';
import {
  GET_PROJECT_COURSES,
  CREATE_COURSE,
  type ProjectCoursesData,
  type ProjectCoursesVariables,
  type CreateCourseData,
  type CreateCourseVariables,
  type CourseListItem,
} from '../../graphql/courses';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

// Translation keys for the course lifecycle status shown in each list row.
const COURSE_STATUS_KEYS: Record<CourseStatus, TranslationKey> = {
  draft: 'courseStatus.draft',
  proposed: 'courseStatus.proposed',
  approved: 'courseStatus.approved',
  generating: 'courseStatus.generating',
  ready: 'courseStatus.ready',
};

function courseStatusLabel(t: I18n['t'], status: string): string {
  const key = COURSE_STATUS_KEYS[status as CourseStatus];
  return key === undefined ? status : t(key);
}

// Cursos tab: lists the project's courses (title + status, each linking to its
// Course detail screen) and — for owners/editors — a manual create form
// (createCourse). Viewers get a read-only list. Membership/role is enforced
// server-side; the UI mirrors it for immediate feedback (Req 4.1, 4.3, 4.7).
//
// ─────────────────────────────────────────────────────────────────────────
// EXTENSION POINT FOR TASK 16.5 (agent course generation)
// Task 16.5 adds the "Generar curso con agente" flow (agentGenerateCourse +
// structure review/approval) as its OWN component and its OWN graphql file.
// Slot that component into the marked <section> below — see the comment at the
// `agent-generate-slot` element. This task (16.4) deliberately does NOT
// implement the agent form so the two tasks stay decoupled.
// ─────────────────────────────────────────────────────────────────────────
export function CoursesTab(): ReactNode {
  const { projectId, canEdit } = useOutletContext<ProjectTabContext>();
  const { t } = useT();

  const { data, loading, error, refetch } = useQuery<ProjectCoursesData, ProjectCoursesVariables>(
    GET_PROJECT_COURSES,
    { variables: { projectId }, fetchPolicy: 'cache-and-network' },
  );

  const courses = useMemo<ReadonlyArray<CourseListItem>>(
    () => data?.getProject?.courses ?? [],
    [data],
  );

  const [showForm, setShowForm] = useState(false);

  if (loading && data === undefined) {
    return (
      <div className="tab-panel">
        <p>{t('courses.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-panel">
        <p className="form-error">{t('courses.loadError', { message: error.message })}</p>
        <button type="button" onClick={() => void refetch()}>
          {t('courses.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="tab-panel">
      <div className="tab-panel-header">
        <h3>{t('courses.title')}</h3>
        {canEdit && (
          <button type="button" onClick={() => setShowForm((open) => !open)}>
            {showForm ? t('courses.close') : t('courses.createManually')}
          </button>
        )}
      </div>

      {canEdit && showForm && (
        <CreateCourseForm
          projectId={projectId}
          onCreated={async () => {
            setShowForm(false);
            await refetch();
          }}
        />
      )}

      {/*
        agent-generate-slot — TASK 16.5 inserts its <AgentGenerateSection /> here.
        Keep the agent-generation UI in its own component/file; render it in this
        section (gated by `canEdit`) and refetch the course list on success.
        16.4 owns only the manual create above and the course list below.
      */}
      {canEdit && (
        <AgentGenerateSection
          projectId={projectId}
          onCourseCreated={async () => {
            await refetch();
          }}
        />
      )}

      {courses.length === 0 ? (
        <p className="tab-placeholder">{t('courses.empty')}</p>
      ) : (
        <ul className="course-list">
          {courses.map((course) => (
            <li key={course.courseId} className="course-row">
              <Link
                to={`/projects/${projectId}/courses/${course.courseId}`}
                className="course-row-title"
              >
                {course.title}
              </Link>
              <span className={`course-status course-status-${course.status}`}>
                {courseStatusLabel(t, course.status)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface CreateCourseFormProps {
  projectId: string;
  onCreated(): Promise<void> | void;
}

// Owner/editor-only manual create form. Requires a title (1–200 chars) and a
// topic prompt (1–2000 chars), validated client-side with the shared domain
// validators before calling `createCourse` so invalid input never reaches the
// API and the creator gets immediate feedback (Req 4.4, 4.5, 4.6). Language is
// left to the server default (Spanish) per Req 4.8.
function CreateCourseForm({ projectId, onCreated }: CreateCourseFormProps): ReactNode {
  const { t } = useT();
  const [title, setTitle] = useState('');
  const [topicPrompt, setTopicPrompt] = useState('');
  const [validationError, setValidationError] = useState<string | undefined>(undefined);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  const [createCourse, { loading }] = useMutation<CreateCourseData, CreateCourseVariables>(
    CREATE_COURSE,
  );

  const titleHint = t('courses.form.titleHint', { min: COURSE_TITLE_MIN, max: COURSE_TITLE_MAX });
  const promptHint = t('courses.form.topicHint', { min: TOPIC_PROMPT_MIN, max: TOPIC_PROMPT_MAX });

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitError(undefined);

    const titleResult = validateCourseTitle(title);
    if (!titleResult.ok) {
      setValidationError(
        t('courses.form.titleInvalid', { min: COURSE_TITLE_MIN, max: COURSE_TITLE_MAX }),
      );
      return;
    }
    const promptResult = validateTopicPrompt(topicPrompt);
    if (!promptResult.ok) {
      setValidationError(
        t('courses.form.topicInvalid', { min: TOPIC_PROMPT_MIN, max: TOPIC_PROMPT_MAX }),
      );
      return;
    }
    setValidationError(undefined);

    try {
      await createCourse({
        variables: { input: { projectId, title, topicPrompt } },
      });
      setTitle('');
      setTopicPrompt('');
      await onCreated();
    } catch (err) {
      setSubmitError(errorMessage(err, t('courses.form.createError')));
    }
  }

  return (
    <form className="course-form" onSubmit={handleSubmit}>
      <label>
        {t('courses.form.title')}
        <input
          type="text"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (validationError) {
              setValidationError(undefined);
            }
          }}
          placeholder={t('courses.form.titlePlaceholder')}
          required
        />
      </label>
      <p className="field-hint">{titleHint}</p>

      <label>
        {t('courses.form.topic')}
        <textarea
          value={topicPrompt}
          onChange={(e) => {
            setTopicPrompt(e.target.value);
            if (validationError) {
              setValidationError(undefined);
            }
          }}
          placeholder={t('courses.form.topicPlaceholder')}
          rows={3}
          required
        />
      </label>
      <p className="field-hint">{promptHint}</p>

      {validationError && <p className="form-error">{validationError}</p>}
      {submitError && <p className="form-error">{submitError}</p>}

      <div className="course-form-actions">
        <button type="submit" disabled={loading}>
          {loading ? t('courses.form.creating') : t('courses.form.create')}
        </button>
      </div>
    </form>
  );
}
